// Hızlı sayım: depo + kategori seç, hemen say. Planlama ve atama yok; sayımı açan kişi sayar.
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, InputAdornment,
  List, ListItemButton, ListItemText, MenuItem, Radio, Switch, TextField, Typography,
} from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../contexts/AuthContext';
import { WarehouseService, type InventoryWarehouseDto } from '../services/warehouseService';
import { CategoryService, type InventoryCategoryDto } from '../services/categoryService';
import { stockCountService } from './stockCountService';

const LAST_WAREHOUSE_KEY = 'stockCount.quick.lastWarehouse';

interface Props {
  open: boolean;
  onClose: () => void;
  onStarted: (id: string) => void;
  warehouseFilter?: (w: InventoryWarehouseDto) => boolean;
}

const QuickCountDialog: React.FC<Props> = ({ open, onClose, onStarted, warehouseFilter }) => {
  const { i18n } = useTranslation();
  const tt = (tr: string, en: string) => ((i18n.language || '').startsWith('en') ? en : tr);
  const { userDetails } = useAuth() as any;
  const companyId: string = userDetails?.companyId || '';

  const [warehouses, setWarehouses] = useState<InventoryWarehouseDto[]>([]);
  const [categories, setCategories] = useState<InventoryCategoryDto[]>([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [filter, setFilter] = useState('');
  const [showExpected, setShowExpected] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !companyId) return;
    setError(null);
    setCategoryId('');
    setFilter('');
    WarehouseService.getAllWarehouses(companyId).then((list) => {
      const normal = list.filter((w) => (w.warehouseType || 'Normal') !== 'Smart' && (!warehouseFilter || warehouseFilter(w)));
      setWarehouses(normal);
      let last = '';
      try { last = localStorage.getItem(LAST_WAREHOUSE_KEY) || ''; } catch { /* yok say */ }
      setWarehouseId(normal.some((w) => w.id === last) ? last : (normal.length === 1 ? normal[0].id : (normal.find((w) => (w as any).isDefault)?.id || '')));
    }).catch(() => setWarehouses([]));
    CategoryService.getAllCategories(companyId).then(setCategories).catch(() => setCategories([]));
  }, [open, companyId]);

  const visible = useMemo(() => {
    const f = filter.trim().toLocaleLowerCase('tr-TR');
    return categories
      .filter((c) => !f || c.name.toLocaleLowerCase('tr-TR').includes(f))
      .sort((a, b) => a.name.localeCompare(b.name, 'tr'));
  }, [categories, filter]);

  const start = async () => {
    if (!warehouseId || !categoryId) {
      setError(tt('Depo ve kategori seçin.', 'Select a warehouse and a category.'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      try { localStorage.setItem(LAST_WAREHOUSE_KEY, warehouseId); } catch { /* yok say */ }
      const category = categories.find((c) => c.id === categoryId);
      const created = await stockCountService.create({
        name: category?.name,
        type: 'QUICK',
        warehouseId,
        scope: { categoryIds: [categoryId], brandIds: [], itemIds: [], includeZeroStock: true, includeInactive: false },
        isBlind: !showExpected,
        assignees: [],
        startNow: true,
      });
      onStarted(created.id);
    } catch (e: any) {
      setError(e?.message || tt('Hızlı sayım başlatılamadı.', 'Quick count could not be started.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
        <BoltIcon color="success" /> {tt('Hızlı Sayım', 'Quick Count')}
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'grid', gap: 1.5 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField select size="small" label={tt('Depo', 'Warehouse')} value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
          {warehouses.map((w) => <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>)}
        </TextField>
        <TextField size="small" placeholder={tt('Kategori ara…', 'Search category…')} value={filter} onChange={(e) => setFilter(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        <Box sx={{ maxHeight: 320, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <List dense disablePadding>
            {visible.map((c) => (
              <ListItemButton key={c.id} selected={c.id === categoryId} onClick={() => setCategoryId(c.id)}>
                <Radio size="small" checked={c.id === categoryId} sx={{ p: 0.5, mr: 1 }} />
                <ListItemText primary={c.name} secondary={c.parentName || undefined} />
              </ListItemButton>
            ))}
            {visible.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>{tt('Kategori bulunamadı.', 'No category found.')}</Typography>
            )}
          </List>
        </Box>
        <FormControlLabel control={<Switch checked={showExpected} onChange={(e) => setShowExpected(e.target.checked)} />}
          label={tt('Sistemdeki miktarı göster', 'Show system quantity')} />
        <Typography variant="caption" color="text.secondary">
          {tt('Hızlı sayım kısmi sayımdır: yalnız saydığınız ürünlerin stoğu düzelir. Kaydedip çıkabilir, sonra devam edebilirsiniz; stok yalnız kontrol edip onaylayınca değişir.',
            'Quick count is partial: only counted products are adjusted. You can leave and continue later; stock changes only when you approve.')}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} disabled={busy}>{tt('Vazgeç', 'Cancel')}</Button>
        <Button variant="contained" color="success" onClick={start} disabled={busy || !categoryId || !warehouseId}>
          {tt('Saymaya başla', 'Start counting')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickCountDialog;
